import { authAxios } from "../utils/session_utils";

const BASE = `${process.env.REACT_APP_BACK_END_API}/episode-scoring`;

export type ScoringMode = "load_and_score" | "rescore";

export interface ScoringJob {
  id: string;
  status: string;
  mode: ScoringMode;
  fileName: string | null;
  userEmail: string;
  createdAt: string;
  finishedAt: string | null;
  durationSeconds: number | null;
  rowsInFile: number | null;
  rowsInserted: number | null;
  rowsUpdated: number | null;
  rowsUnchanged: number | null;
  duplicateRowsInFile: number | null;
  nothingNew: boolean;
  episodesScored: number | null;
  feedHealthWarnings: { platform: string; channel: string; month: string; hov_ratio: number }[];
  errorMessage: string | null;
  steps: Record<string, unknown>[];
}

export interface CoverageRow {
  platform: string;
  channel: string;
  firstDay: string;
  lastDay: string;
  rows: number;
  lastLoadedAt: string | null;
}

export interface CoverageMonthRow {
  month: string;
  rows: number;
  days: number;
  firstDay: string;
  lastDay: string;
  minutes: number;
  lastLoadedAt: string | null;
}

export interface EpisodeAiringRow {
  platform: string;
  channel: string;
  startLocal: string;
  dayOfWeek: number;
  hourOfDay: number;
  durationMin: number;
  viewershipMin: number;
  rate: number;
  expectedRate: number;
  baselineIndex: number;
  seriesFactor: number;
  airingPct: number;
  mergedRows: number;
}

export interface ScoreRow {
  REF_ID: string;
  SERIES: string;
  TITLE: string;
  SCOPE: string;
  FEED_CODE: string | null;
  PLATFORM: string | null;
  SCORE_PCT: number;
  BUCKET: string;
  AIRINGS: number;
  CONFIDENCE: string;
  SOURCE: string;
  WEIGHTING: string | null;
  AS_OF_DATE: string;
  BATCH_JOB_ID: string;
}

export interface ScoreFilters {
  scope: "overall" | "feed" | "platform";
  feed?: string;
  platform?: string;
  series?: string;
  confidence?: "strong" | "thin";
  q?: string;
}

export interface ScoreFilterOptions {
  feeds: string[];
  platforms: string[];
  series: string[];
  asOfDate: string | null;
  computedAt: string | null;
}

export const submitJob = async (mode: ScoringMode, file: File | null, toEmail: string, timezoneUniversal: boolean) => {
  const form = new FormData();
  form.append("mode", mode);
  form.append("toEmail", toEmail);
  form.append("timezoneUniversal", String(timezoneUniversal));
  if (file) form.append("file", file);
  const response = await authAxios({ method: "POST", url: `${BASE}/jobs`, data: form, headers: { "Content-Type": "multipart/form-data" } });
  return response.data as { batchJobId: string; awsBatchJobId: string };
};

export const fetchJobs = async (current: number, pageSize: number) => {
  const pagination = encodeURIComponent(JSON.stringify({ current, pageSize }));
  const response = await authAxios({ method: "GET", url: `${BASE}/jobs?pagination=${pagination}` });
  return response.data as { rows: ScoringJob[]; total: number };
};

export const fetchCoverage = async () => {
  const response = await authAxios({ method: "GET", url: `${BASE}/coverage` });
  return response.data as { rows: CoverageRow[] };
};

export const fetchCoverageMonths = async (platform: string, channel: string) => {
  const params = new URLSearchParams({ platform, channel });
  const response = await authAxios({ method: "GET", url: `${BASE}/coverage/months?${params.toString()}` });
  return response.data as { rows: CoverageMonthRow[] };
};

export const fetchEpisodeAirings = async (refId: string, scope: { feed?: string | null; platform?: string | null }) => {
  const params = new URLSearchParams();
  if (scope.feed) params.set("feed", scope.feed);
  if (scope.platform) params.set("platform", scope.platform);
  const response = await authAxios({ method: "GET", url: `${BASE}/episodes/${encodeURIComponent(refId)}/airings?${params.toString()}` });
  return response.data as { rows: EpisodeAiringRow[] };
};

const filterQuery = (filters: ScoreFilters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  return params;
};

export const fetchScores = async (filters: ScoreFilters, current: number, pageSize: number) => {
  const params = filterQuery(filters);
  params.set("pagination", JSON.stringify({ current, pageSize }));
  const response = await authAxios({ method: "GET", url: `${BASE}/scores?${params.toString()}` });
  return response.data as { rows: ScoreRow[]; total: number; totalAllConfidence: number };
};

export const fetchScoreFilterOptions = async () => {
  const response = await authAxios({ method: "GET", url: `${BASE}/scores/filters` });
  return response.data as ScoreFilterOptions;
};

export const downloadScoresCsv = async (filters: ScoreFilters) => {
  const params = filterQuery(filters);
  const response = await authAxios({ method: "GET", url: `${BASE}/scores.csv?${params.toString()}`, responseType: "blob" });
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `episode_scores_${filters.scope}${filters.feed ? "_" + filters.feed : ""}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
